import { useEffect, useRef, useState } from 'react';
import { useNavbarData } from '../../hooks/useNavbarData';
import { AuthButton } from './AuthButton';
import { CurrencyLangSelect } from './CurrencyLangSelect';
import { MobileNavItemsList } from './MobileNavItemsList';
import { NavbarLink } from './NavbarLink';
import { useDeviceSizeListener } from '../../hooks/common/mobile/useDeviceSizeListener';
import Icon, { iconNames } from '../common/icon';
import { COLORS } from '../../constants';
import { useTranslation } from 'react-i18next';
import { TextAdvice } from '../common/text/TextAdvice';
import { useSelector } from 'react-redux';
import { selectAccountId } from '../../redux/reducers/account/account';
import { Link } from '../common/buttons';
import { BrandName } from './BrandName';

export const NavbarContent = () => {
    const { t } = useTranslation();
    const { navItems, isAuthenticated } = useNavbarData();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const accountId = useSelector(selectAccountId);
    const menuRef = useRef(null);

    const { isMobile } = useDeviceSizeListener({ breakpoint: 1024 });

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setIsMenuOpen(false);
            }
        };
        if (isMenuOpen && isMobile) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () =>
            document.removeEventListener('mousedown', handleClickOutside);
    }, [isMenuOpen, isMobile]);

    const toggleMenu = () => setIsMenuOpen((isOpen) => !isOpen);
    const closeMenu = () => setIsMenuOpen(false);

    const menuClassName = isMenuOpen ? 'nav__right-open' : 'nav__right-close';

    return isMobile ? (
        <>
            <Link to="/" text={<BrandName />} />
            <div className="nav__right" ref={menuRef}>
                <button
                    type="button"
                    className="nav__right-open-btn"
                    onClick={toggleMenu}
                    aria-label={t('menu')}
                    aria-expanded={isMenuOpen}
                    aria-controls="mobile-nav"
                >
                    <Icon name={iconNames.list} size={24} fill="white" />
                </button>
                <div className={menuClassName} id="mobile-nav">
                    <button
                        type="button"
                        className="nav__right-close-btn"
                        onClick={toggleMenu}
                        aria-label={t('close')}
                    >
                        <Icon
                            name={iconNames.close}
                            size={25}
                            fill={COLORS.GRAY_LIGHT}
                        />
                    </button>
                    <AuthButton onClose={closeMenu} />
                    {navItems.map((item, index) => (
                        <NavbarLink
                            key={item.to || item.text || index}
                            to={item.to}
                            text={item.text}
                            icon={item.icon}
                            isMobile
                            onClose={closeMenu}
                        />
                    ))}
                    <CurrencyLangSelect />
                    <MobileNavItemsList
                        isAuthenticated={isAuthenticated}
                        onClose={closeMenu}
                    />
                    <div style={{ paddingTop: !accountId ? '5.5rem' : '0' }}>
                        <TextAdvice warning>{t('useLightTheme')}</TextAdvice>
                    </div>
                </div>
            </div>
        </>
    ) : (
        <>
            <div style={{ flex: 1, justifySelf: 'flex-start' }}>
                <Link to="/" text={<BrandName />} />
            </div>
            <div style={{ display: 'flex', flex: 2, justifyContent: 'center' }}>
                {navItems.map((item, index) => (
                    <NavbarLink
                        key={item.to || item.text || index}
                        to={item.to}
                        text={item.text}
                    />
                ))}
            </div>
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem',
                    justifyContent: 'flex-end',
                    flex: 1,
                }}
            >
                <CurrencyLangSelect />
                <AuthButton />
            </div>
        </>
    );
};
